import React from "react"

import {
    mdiEmoticonExcited,
    mdiEmoticonNeutral,
    mdiEmoticonSad,
} from "@mdi/js"
import Icon from "@mdi/react"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import MapOutlinedIcon from "@mui/icons-material/MapOutlined"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	Tab,
	Tabs,
	Stack,
	Typography,
} from "@mui/material"
import {
    GoogleMap,
    InfoWindow,
    LoadScript,
    Marker,
} from "@react-google-maps/api"

import {
    ErrorResponse,
    Loading,
    RestResponse,
} from "@sinossi/mates-react-library"

import {
    t,
    Trans,
} from "@lingui/macro"

import {
    PageHeader,
    RoutesData,
} from "components/commons"
import { SbSnackbar } from "components/commons/sb-snackbar"
import {
    AcceptMatchModal,
    RefuseMatchModal,
} from "components/match"
import { RefuseModalForm } from "components/match/refuse-modal-form"
import {
    MatesComponent,
    MatesComponentState,
} from "components/mates-component"
import { EditorState } from "draft-js"
import {
	AccountType,
	Asset,
	AssetMatchBrand,
	AssetMatchesBrandStatus,
	Document,
    DocumentType,
    OrderType,
    PaginatedResponse,
    SearchFilter,
    SessionResponse,
} from "models"
import { AssetDocumentsSection } from "pages/asset/asset-details/components/asset-documents-section"
import { AssetService } from "services/asset.service"
import { MatchService } from "services/match.service"
import { MessageService } from "services/message.service"
import withUseRouter, { UseRouterParams } from "services/router"
import {
    getAiAnalysisNumber,
    getAiAnalysisValue,
    normalizeAiAnalysis,
} from "utils/ai-analysis.utils"
import { ContactMessageDialog } from "./components/contact-message-dialog"
import { MatchAssetSummarySection } from "./components/match-asset-summary-section"
import { MatchOverviewSection } from "./components/match-overview-section"

const TERMS_AND_CONDITIONS_URL = "https://www.storebook.it/termini-e-condizioni"

interface MatchDetailsProps extends UseRouterParams {
    sessionData: SessionResponse
}

interface MatchDetailsState extends MatesComponentState {
	loading: boolean
	acceptModalOpened: boolean
	refuseModalOpened: boolean
	contactModalOpened: boolean
    contactSubject: string
    contactMessageEditorState: EditorState
    sendingContactMessage: boolean
	documentsFilter: SearchFilter
	documentsData: PaginatedResponse<Document> | null
	photoFilter: SearchFilter
	photoData: PaginatedResponse<Document> | null
	match: AssetMatchBrand | null
	selectedPoiIndex: number | null
	requestingAssessment: boolean
	assessmentModalOpened: boolean
	interestModalOpened: boolean
	interestDisclaimerAccepted: boolean
	interestSubmitting: boolean
	neighborhoodTab: NeighborhoodTab
	snackbarData: {
        openNotification: boolean,
        severity: "error" | "info" | "success" | "warning",
        notificationMessage: string
    }
}

interface PoiEntry {
	name?: string
	nome?: string
	address?: string
	indirizzo?: string
	lat?: number
	lng?: number
	place_id?: string
	brand_nome?: string
	brand?: string | null
	brand_riconosciuto?: boolean | string | null
}

type NeighborhoodTab = "brand" | "poi"
const NEIGHBORHOOD_MAX_VISIBLE_ITEMS = 8

class MatchDetails extends React.Component<MatchDetailsProps, MatchDetailsState> {
    private readonly matesComponent: MatesComponent<MatchDetailsProps, MatchDetailsState> = new MatesComponent(this)
    private activeLoadRequestId = 0
    private readonly interestDisclaimerHtml = `
<p>Cliccando su "Comunica Interesse", il Brand dichiara di aver preso visione delle condizioni economiche del servizio di intermediazione immobiliare offerto da STOREBOOK e di accettarle integralmente.</p>
<p>In particolare, il Brand prende atto che, qualora la manifestazione di interesse si traduca nella conclusione di un contratto di locazione o acquisto dell'immobile proposto, sarà dovuta a STOREBOOK una commissione di intermediazione nella misura e con le modalità indicate nei Termini e Condizioni Generali del Servizio, consultabili all'indirizzo <a href="${TERMS_AND_CONDITIONS_URL}" target="_blank" rel="noopener noreferrer">${TERMS_AND_CONDITIONS_URL}</a>.</p>
<p>La semplice manifestazione di interesse non comporta alcun obbligo o vincolo per il Brand, fermo restando l'impegno a riconoscere il ruolo di intermediazione di STOREBOOK in caso di perfezionamento dell'operazione.</p>
`
    private get matchId(): string {
        return this.props.params.matchId
    }

    constructor(props: MatchDetailsProps) {
        super(props)

        this.state = {
            ...this.matesComponent.setupState(),
            loading: true,
            match: null,
            acceptModalOpened: false,
            refuseModalOpened: false,
            contactModalOpened: false,
            contactSubject: "",
			contactMessageEditorState: EditorState.createEmpty(),
			sendingContactMessage: false,
			documentsFilter: {
				pageSize: 50,
				pageId: 0,
				orderBy: "uploadDate",
				orderType: OrderType.DESCENDING,
			},
			documentsData: null,
			photoFilter: {
				pageSize: 100,
				pageId: 0,
				orderType: null,
				orderBy: null,
			},
			photoData: null,
			selectedPoiIndex: null,
			requestingAssessment: false,
			assessmentModalOpened: false,
			interestModalOpened: false,
			interestDisclaimerAccepted: false,
			interestSubmitting: false,
			neighborhoodTab: "brand",
			snackbarData: {
				openNotification: false,
				severity: "info",
                notificationMessage: "",
            },
        }
    }

    componentDidMount() {
        this.loadData()
    }

    componentDidUpdate(prevProps: MatchDetailsProps) {
        if (prevProps.params.matchId !== this.props.params.matchId) {
			this.setState(
				() => ({
					match: null,
					documentsData: null,
					photoData: null,
					neighborhoodTab: "brand",
					selectedPoiIndex: null,
					requestingAssessment: false,
					assessmentModalOpened: false,
				}),
                this.loadData
            )
        }
    }

    componentWillUnmount() {
        this.activeLoadRequestId += 1
    }

	private isLoadRequestActive = (requestId: number): boolean => requestId === this.activeLoadRequestId

	private handleNeighborhoodTabChange = (_event: React.SyntheticEvent, value: NeighborhoodTab) => {
		this.setState(() => ({ neighborhoodTab: value }))
	}

    private loadData = (): void => {
        const requestId = ++this.activeLoadRequestId
        this.setState(() => ({ loading: true }))
        MatchService.getMatchById(
            this.matchId,
            (response: RestResponse<AssetMatchBrand, ErrorResponse>) => this.getMatchCallback(response, requestId),
            (response: RestResponse<AssetMatchBrand, ErrorResponse>) => this.getMatchCallbackError(response, requestId)
        )
    }

    private getMatchCallback = (response: RestResponse<AssetMatchBrand, ErrorResponse>, requestId: number): void => {
        if (!this.isLoadRequestActive(requestId)) {
            return
        }

        if (response.hasError()) {
            this.matesComponent.onException(response)
            return
        }

		this.setState(
			() => ({
				match: response.data,
				photoData: null,
				neighborhoodTab: "brand",
			}),
			() => {
				this.getPhotos(requestId)
				this.getDocuments(requestId)
			}
		)
    }

	private getMatchCallbackError = (response: RestResponse<AssetMatchBrand, ErrorResponse>, requestId: number): void => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.setState(() => ({ match: null, photoData: null, neighborhoodTab: "brand", loading: false }))
		this.matesComponent.onException(response)
	}

	private getDocuments = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

        if (!this.state.match) {
            this.setState(() => ({ loading: false }))
            return
        }

        const assetId = this.state.match.asset.id

        AssetService.getDocuments(
            assetId,
            DocumentType.ATTACHMENT,
            this.state.documentsFilter,
            (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getDocumentsCallback(response, assetId, requestId),
            (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getDocumentsCallbackError(response, requestId),
            true
        )
    }

    private getDocumentsCallback = (
        response: RestResponse<PaginatedResponse<Document>, ErrorResponse>,
        assetId: string,
        requestId: number
    ) => {
        if (!this.isLoadRequestActive(requestId)) {
            return
        }

        if (response.hasError()) {
            this.matesComponent.onException(response)
            return
        }

        const attachmentsData = response.data
        if (!attachmentsData) {
            this.setState(() => ({
                documentsData: null,
                loading: false,
            }))
            return
        }

		this.fetchAiReportDocuments(assetId, attachmentsData, requestId)
	}

	private getPhotos = (requestId: number = this.activeLoadRequestId) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		const assetId = this.state.match?.asset.id
		if (!assetId) {
			this.setState(() => ({ photoData: null }))
			return
		}

		AssetService.getPhotos(
			assetId,
			DocumentType.PHOTO,
			this.state.photoFilter,
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getPhotosCallback(response, requestId),
			(response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getPhotosCallbackError(response, requestId)
		)
	}

	private getPhotosCallback = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.setState(() => ({ photoData: null }))
			return
		}

		this.setState(() => ({ photoData: response.data }))
	}

	private getPhotosCallbackError = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
		if (!this.isLoadRequestActive(requestId)) {
			return
		}

		this.matesComponent.onException(response)
		this.setState(() => ({ photoData: null }))
	}

    private getDocumentsCallbackError = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>, requestId: number) => {
        if (!this.isLoadRequestActive(requestId)) {
            return
        }

        this.setState(() => ({ documentsData: null, loading: false }))
        this.matesComponent.onException(response)
    }

    private fetchAiReportDocuments = (assetId: string, attachmentsData: PaginatedResponse<Document>, requestId: number) => {
        AssetService.getDocuments(
            assetId,
            DocumentType.AI_REPORT,
            this.state.documentsFilter,
            (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getAiReportDocumentsCallback(response, attachmentsData, requestId),
            (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => this.getAiReportDocumentsCallbackError(response, attachmentsData, requestId),
            false,

            this.state.match?.brand.id
        )
    }

    private getAiReportDocumentsCallback = (
        response: RestResponse<PaginatedResponse<Document>, ErrorResponse>,
        attachmentsData: PaginatedResponse<Document>,
        requestId: number
    ) => {
        if (!this.isLoadRequestActive(requestId)) {
            return
        }

        if (response.hasError()) {
            this.matesComponent.onException(response)
            this.finalizeDocumentsLoading(attachmentsData)
            return
        }

        const mergedDocuments = this.mergeDocumentsData(attachmentsData, response.data)
        this.finalizeDocumentsLoading(mergedDocuments)
    }

    private getAiReportDocumentsCallbackError = (
        response: RestResponse<PaginatedResponse<Document>, ErrorResponse>,
        attachmentsData: PaginatedResponse<Document>,
        requestId: number
    ) => {
        if (!this.isLoadRequestActive(requestId)) {
            return
        }

        this.matesComponent.onException(response)
        this.finalizeDocumentsLoading(attachmentsData)
    }

    private finalizeDocumentsLoading = (documentsData: PaginatedResponse<Document>) => {
        this.setState(() => ({
            documentsData,
            loading: false,
        }))
    }

    private mergeDocumentsData = (
        baseDocuments: PaginatedResponse<Document>,
        extraDocuments?: PaginatedResponse<Document> | null
    ): PaginatedResponse<Document> => {
        const extraItems = extraDocuments?.items ?? []

        if (extraItems.length === 0) {
            return baseDocuments
        }

        const basePagination = baseDocuments.pagination
        const updatedPagination = basePagination
            ? {
                ...basePagination,
                pageCount: basePagination.pageCount + extraItems.length,
                totalRecords: basePagination.totalRecords + extraItems.length,
            }
            : basePagination

        return {
            ...baseDocuments,
            items: [...baseDocuments.items, ...extraItems],
            pagination: updatedPagination,
        }
    }

    private extractDocumentBrandId = (document: Document): string | null => {
        const docBrand = document.brand
        if (docBrand && typeof docBrand === "object" && "id" in docBrand) {
            return docBrand.id ?? null
        }

        if (typeof docBrand === "string") {
            return docBrand
        }

        return document.brandId ?? null
    }

    private extractDocumentBrandName = (document: Document): string | null => {
        const docBrand = document.brand
        if (docBrand && typeof docBrand === "object" && "name" in docBrand) {
            return docBrand.name ?? null
        }

        return document.brandName ?? null
    }

    private findAiReportDocument = (documents: Document[], brandId: string | null, brandName: string | null): Document | null => {
        const aiReports = documents.filter((doc) => doc.documentType === DocumentType.AI_REPORT)

        if (aiReports.length === 0) {
            return null
        }

        if (!brandId && !brandName) {
            return aiReports[0] ?? null
        }

        if (brandId) {
            const byId = aiReports.find((doc) => this.extractDocumentBrandId(doc) === brandId)
            if (byId) {
                return byId
            }
        }

        if (brandName) {
            const targetName = brandName.toLowerCase()
            const byName = aiReports.find((doc) => {
                const docBrandName = this.extractDocumentBrandName(doc)
                return docBrandName?.toLowerCase() === targetName
            })

            if (byName) {
                return byName
            }
        }

        return null
    }

    private openRefuseMatchModal = (): void => {
        this.setState(() => ({ refuseModalOpened: true }))
    }

    private openAcceptMatchModal = (): void => {
        this.setState(() => ({ acceptModalOpened: true }))
    }

    private closeAcceptModal = (): void => {
        this.setState(() => ({ acceptModalOpened: false }))
    }

    private closeRefuseModal = (): void => {
        this.setState(() => ({ refuseModalOpened: false }))
    }

    private openContactModal = (): void => {
        const asset = this.state.match?.asset
        const defaultSubject = asset
            ? `Contatto per ${this.getAssetTitle(asset)}`
            : "Contatto proposta immobile"

        this.setState(() => ({
            contactModalOpened: true,
            contactSubject: defaultSubject,
            contactMessageEditorState: EditorState.createEmpty(),
        }))
    }

    private closeContactModal = (): void => {
        if (this.state.sendingContactMessage) {
            return
        }

        this.setState(() => ({
            contactModalOpened: false,
            contactSubject: "",
            contactMessageEditorState: EditorState.createEmpty(),
        }))
    }

    private handleContactMessageEditorChange = (editorState: EditorState): void => {
        this.setState(() => ({ contactMessageEditorState: editorState }))
    }

    private sendContactMessage = (): void => {
        const accountManagerId = this.state.match?.proposta?.accountManager?.id
        if (!accountManagerId) {
            this.handleOpenNotification(
                t({ id: "match-details.contact.error.no-manager", message: "Account manager non disponibile." }),
                "error"
            )
            return
        }

        const contentState = this.state.contactMessageEditorState.getCurrentContent()
        if (!this.state.contactSubject.trim() || !contentState.hasText()) {
            return
        }

        this.setState(() => ({ sendingContactMessage: true }))

        MessageService.createConversation(
            {
                subject: this.state.contactSubject.trim(),
                participantIds: [accountManagerId],
                initialMessage: contentState.getPlainText(),
            },
            (response: any) => {
                const conversationId = response?.data?.id
                this.setState(() => ({
                    sendingContactMessage: false,
                    contactModalOpened: false,
                    contactSubject: "",
                    contactMessageEditorState: EditorState.createEmpty(),
                }))
                this.handleOpenNotification(
                    t({ id: "match-details.contact.success", message: "Messaggio inviato con successo." }),
                    "success"
                )
                if (conversationId) {
                    this.props.navigate(`/messages?conversation=${conversationId}`)
                }
            },
            (error: any) => {
                this.setState(() => ({ sendingContactMessage: false }))
                this.matesComponent.onException(error)
            }
        )
    }

    private openInterestModal = (): void => {
        this.setState(() => ({
            interestModalOpened: true,
            interestDisclaimerAccepted: false,
        }))
    }

    private closeInterestModal = (): void => {
        if (this.state.interestSubmitting) {
            return
        }

        this.setState(() => ({
            interestModalOpened: false,
            interestDisclaimerAccepted: false,
        }))
    }

    private handleInterestDisclaimerChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
        this.setState(() => ({ interestDisclaimerAccepted: checked }))
    }

    private confirmInterest = (): void => {
        if (!this.state.interestDisclaimerAccepted || this.state.interestSubmitting) {
            return
        }

        this.setState(() => ({
            loading: true,
            interestSubmitting: true,
        }))

        MatchService.setInterested(
            this.matchId,
            this.handleSetInterestedResponse,
            this.handleInterestError
        )
    }

    private handleSetInterestedResponse = (response: RestResponse<any, ErrorResponse>): void => {
        if (response.hasError()) {
            this.handleInterestError(response)
            return
        }

        MatchService.makeMatchVisibleToBrand(
            this.matchId,
            this.handleMakeVisibleResponse,
            this.handleInterestError
        )
    }

    private handleMakeVisibleResponse = (response: RestResponse<any, ErrorResponse>): void => {
        if (response.hasError()) {
            this.handleInterestError(response)
            return
        }

        this.setState(() => ({
            interestSubmitting: false,
            interestModalOpened: false,
            interestDisclaimerAccepted: false,
        }))

        this.onMatchResponse(response)
    }

    private handleInterestError = (error: RestResponse<any, ErrorResponse> | any): void => {
        this.setState(() => ({
            loading: false,
            interestSubmitting: false,
        }))

        this.matesComponent.onException(error)
    }

    private handleRequestAssessment = (): void => {
        const userIsBrand = this.props.sessionData.accountType === AccountType.BRAND
        if (!userIsBrand || this.state.requestingAssessment || !this.state.match) {
            return
        }

        this.setState(() => ({ requestingAssessment: true }))

        MatchService.requestAssessment(
            this.matchId,
            () => {
                this.setState(() => ({
                    requestingAssessment: false,
                    assessmentModalOpened: true,
                }))
            },
            (error: RestResponse<any, ErrorResponse>) => {
                this.setState(() => ({ requestingAssessment: false }))
                this.matesComponent.onException(error)
            }
        )
    }

    private closeAssessmentModal = (): void => {
        this.setState(() => ({ assessmentModalOpened: false }))
    }

    private acceptMatchModalCallback = (): void => {
        this.setState(() => ({
            acceptModalOpened: false,
            loading: true,
        }))

        MatchService.approveMatch(this.matchId, this.onMatchResponse, this.matesComponent.onException)
    }

    private refuseMatchModalCallback = (values: RefuseModalForm): void => {
        this.setState(() => ({
            refuseModalOpened: false,
            loading: true,
        }))

        MatchService.rejectMatch(
            this.matchId,
            {
                reason: values.reason,
                description: values.description,
            },
            this.onMatchResponse,
            this.matesComponent.onException
        )
    }

    private onMatchResponse = (response: RestResponse<any, ErrorResponse>): void => {
        if (response.hasError()) {
            this.matesComponent.onException(response)
            return
        }

        this.loadData()
    }

    private handleOpenNotification = (message: string, severity: "error" | "info" | "success" | "warning") => {
        this.setState(() => ({
            snackbarData: {
                openNotification: true,
                notificationMessage: message,
                severity,
            },
        }))
    }

    private handleCloseNotification = () => {
        this.setState(() => ({
            snackbarData: {
                ...this.state.snackbarData,
                openNotification: false,
            },
        }))
    }

    private formatCurrency = (value?: number | null, fractionDigits: number = 0): string => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return "-"
        }

        return new Intl.NumberFormat("it-IT", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        }).format(value)
    }

    private formatNumber = (value?: number | null, fractionDigits: number = 0): string => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return "-"
        }

        return value.toLocaleString("it-IT", {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })
    }

    private formatDate = (value?: Date | string | null): string => {
        if (!value) {
            return "-"
        }

        const parsed = typeof value === "string" ? new Date(value) : value
        if (Number.isNaN(parsed.getTime())) {
            return "-"
        }

        return parsed.toLocaleDateString("it-IT")
    }

    private isToday = (value?: Date | string | null): boolean => {
        if (!value) {
            return false
        }

        const parsed = typeof value === "string" ? new Date(value) : value
        if (Number.isNaN(parsed.getTime())) {
            return false
        }

        const now = new Date()

        return parsed.getDate() === now.getDate() &&
            parsed.getMonth() === now.getMonth() &&
            parsed.getFullYear() === now.getFullYear()
    }

    private formatPercentage = (value?: number | null, signed: boolean = false): string => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return "-"
        }

        const sign = signed && value > 0 ? "+" : ""
        return `${sign}${Math.round(value)}%`
    }

	private isPoiBrandRecognized = (poi: PoiEntry): boolean => {
		const value = poi.brand_riconosciuto
		if (typeof value === "string") {
			return value.toLowerCase() === "true"
		}

		return value === true
	}

	private getPoiName = (poi: PoiEntry, preferBrandName: boolean = false): string | null => {
		const candidates = preferBrandName
			? [poi.brand_nome, poi.nome, poi.name, poi.brand]
			: [poi.nome, poi.name, poi.brand_nome, poi.brand]

		for (const candidate of candidates) {
			if (typeof candidate === "string" && candidate.trim()) {
				return candidate.trim()
			}
		}

		return null
	}

	private getPoiAddress = (poi: PoiEntry): string | null => {
		const candidates = [poi.indirizzo, poi.address]
		for (const candidate of candidates) {
			if (typeof candidate === "string" && candidate.trim()) {
				return candidate.trim()
			}
		}
		return null
	}

    private getAssetTitle = (asset: Asset): string => {
        const address = asset.address ?? ""
        const city = asset.city ?? asset.zone?.city ?? ""

        if (address && city && !address.includes(city)) {
            return `${address} - ${city}`
        }

        return address || city || "-"
    }

    private getMatchReasons = (match: AssetMatchBrand): string[] => {
        return (match.positivePoints ?? []).filter((point) => point && point.trim() !== "")
    }

    private getCompatibilityLevel = (compatibility: number): "high" | "medium" | "low" => {
        const bounded = Math.max(0, Math.min(100, compatibility))
        if (bounded > 60) {
            return "high"
        }
        if (bounded > 33) {
            return "medium"
        }
        return "low"
    }

    private getCompatibilityColor = (level: "high" | "medium" | "low") => {
        switch (level) {
            case "high":
                return "#2e7d32"
            case "medium":
                return "#5aaac3"
            case "low":
            default:
                return "#c62828"
        }
    }

    private renderCompatibilityEmoticon = (compatibility: number) => {
        const level = this.getCompatibilityLevel(compatibility)
        const color = this.getCompatibilityColor(level)
        const iconPath = level === "high"
            ? mdiEmoticonExcited
            : level === "medium"
                ? mdiEmoticonNeutral
                : mdiEmoticonSad

        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon path={iconPath} size={1.2} color={color} />
            </Stack>
        )
    }

    private renderCompatibilityBar = (compatibility: number) => {
        const bounded = Math.max(0, Math.min(100, compatibility))
        const level = this.getCompatibilityLevel(bounded)
        const barColor = this.getCompatibilityColor(level)

        return (
            <Box sx={{ width: 220, height: 18, borderRadius: 999, backgroundColor: "#d7dde7", overflow: "hidden" }}>
                <Box sx={{ width: `${bounded}%`, height: "100%", backgroundColor: barColor }} />
            </Box>
        )
    }

    private resolveMatchPrimaryAction = (status: AssetMatchesBrandStatus): { label: string; onClick: () => void } | null => {
        if (
            status === AssetMatchesBrandStatus.APPROVED ||
            status === AssetMatchesBrandStatus.CONTRACT_DONE ||
            status === AssetMatchesBrandStatus.REJECTED
        ) {
            return null
        }

        if (status === AssetMatchesBrandStatus.SENT || status === AssetMatchesBrandStatus.CREATED) {
            return {
                label: t({ id: "match-details.action.interested", message: "Mi interessa" }),
                onClick: this.openInterestModal,
            }
        }

        return {
            label: t({ id: "match-details.action.approve", message: "Accetta il matching" }),
            onClick: this.openAcceptMatchModal,
        }
    }

    private resolveMatchStatusMessage = (match: AssetMatchBrand): string | null => {
        if (match.status === AssetMatchesBrandStatus.APPROVED) {
            return t({ id: "match-details.status.approved", message: "Match approvato" })
        }

        if (match.status === AssetMatchesBrandStatus.REJECTED) {
            return t({ id: "match-details.status.rejected", message: "Match rifiutato" })
        }

        if (match.status === AssetMatchesBrandStatus.INTERESTED) {
            return t({ id: "match-details.status.interested", message: "Hai manifestato interesse" })
        }

        return null
    }

    render() {
        const match = this.state.match
        const asset = match?.asset ?? null
        const assetDetails = asset?.assetDetails ?? null
        const userIsBrand = this.props.sessionData.accountType === AccountType.BRAND

        const compatibilityValue = Math.max(0, Math.min(100, match?.compatibility ?? 0))
        const matchReasons = match ? this.getMatchReasons(match) : []
        const matchStatusMessage = match ? this.resolveMatchStatusMessage(match) : null
        const rejectionReason = match?.rejectDescription?.trim()
            ? match.rejectDescription.trim()
            : (match?.status === AssetMatchesBrandStatus.REJECTED && match.rejectReason
                ? t({ id: `enums.match-reject-reasons.${match.rejectReason}`, message: match.rejectReason })
                : null)
        const accountManager = match?.proposta?.accountManager ?? null
        const accountManagerId = accountManager?.id ?? null
        const accountManagerName = accountManager?.accountDetails?.firstName && accountManager?.accountDetails?.lastName
            ? `${accountManager.accountDetails.firstName} ${accountManager.accountDetails.lastName}`
            : (accountManager?.username ?? "-")
        const canContactAccountManager = userIsBrand && !!accountManagerId
        const blurSectionsForPartialVisibility = userIsBrand && !!match?.partialVisible
        const blurredSectionSx = blurSectionsForPartialVisibility
            ? {
                filter: "blur(6px)",
                pointerEvents: "none",
                userSelect: "none",
            }
            : {}

        const primaryAction = match && userIsBrand ? this.resolveMatchPrimaryAction(match.status) : null
        const canRefuse = match && userIsBrand ? (
            match.status !== AssetMatchesBrandStatus.APPROVED &&
            match.status !== AssetMatchesBrandStatus.REJECTED &&
            match.status !== AssetMatchesBrandStatus.CONTRACT_DONE
        ) : false

        const zoneLabel = asset?.zone?.name ?? asset?.city ?? "-"
        const assetTitle = asset ? this.getAssetTitle(asset) : "-"
        const priceValue = asset?.characteristics?.price ?? null
        const priceLabel = this.formatCurrency(priceValue)
        const pricePerMq = priceValue && asset?.characteristics?.gla ? priceValue / asset.characteristics.gla : null
        const pricePerMqLabel = pricePerMq !== null ? `${this.formatCurrency(pricePerMq, 2)} /mq` : "€ - /mq"
        const lastUpdateLabel = asset?.lastUpdate
            ? (this.isToday(asset.lastUpdate) ? t({ id: "match-details.last-update.today", message: "oggi" }) : this.formatDate(asset.lastUpdate))
            : t({ id: "match-details.last-update.today", message: "oggi" })

        const highlightChips: string[] = []
        const featureChips: string[] = []
        const premiumLocation = asset?.characteristics?.position === "HIGH_STREET" || asset?.characteristics?.position === "MALL"

        if (asset?.characteristics?.type) {
            highlightChips.push(t({ id: `enums.asset-types.${asset.characteristics.type}`, message: asset.characteristics.type }))
        }
        if (premiumLocation) {
            highlightChips.push(t({ id: "match-details.premium-location", message: "Premium Location" }))
        }

        if (asset?.characteristics?.airConditioning) {
            featureChips.push(t({ id: "match-details.feature.air-conditioning", message: "Climatizzazione" }))
        }
        if (asset?.characteristics?.fireProtection) {
            featureChips.push(t({ id: "match-details.feature.fire-protection", message: "Antincendio" }))
        }
        if (asset?.characteristics?.position) {
            featureChips.push(t({ id: `enums.asset-position.${asset.characteristics.position}`, message: asset.characteristics.position }))
        }
        if (asset?.characteristics?.contractType) {
            featureChips.push(t({ id: `enums.contract-type.${asset.characteristics.contractType}`, message: asset.characteristics.contractType }))
        }

        const bathroomsValue = asset?.characteristics?.toilet !== undefined && asset?.characteristics?.toilet !== null
            ? (asset.characteristics.toilet ? 1 : 0)
            : null

        const aiAnalysisLegacy = (asset as (Asset & { ai_analysis?: unknown }) | null)?.ai_analysis
        const aiAnalysis = normalizeAiAnalysis(asset?.aiAnalysis ?? aiAnalysisLegacy ?? assetDetails?.aiAnalysis)
        const assetGla = asset?.characteristics?.gla ?? null
        const aiMinMqAnno = getAiAnalysisNumber(
            aiAnalysis,
            [
                "valutazione_mercato.quotazione.locazione.min_mq_anno",
                "valutazione_mercato.quotazione.min_mq_anno",
                "quotazione.locazione.min_mq_anno",
            ],
            null
        )
        const aiMaxMqAnno = getAiAnalysisNumber(
            aiAnalysis,
            [
                "valutazione_mercato.quotazione.locazione.max_mq_anno",
                "valutazione_mercato.quotazione.max_mq_anno",
                "quotazione.locazione.max_mq_anno",
            ],
            null
        )

        const aiPriceMin = aiMinMqAnno !== null && assetGla !== null && assetGla > 0
            ? aiMinMqAnno * assetGla
            : assetDetails?.aiPriceMin ?? null
        const aiPriceMax = aiMaxMqAnno !== null && assetGla !== null && assetGla > 0
            ? aiMaxMqAnno * assetGla
            : assetDetails?.aiPriceMax ?? null
        const averageMqPriceInArea = getAiAnalysisNumber(
            aiAnalysis,
            [
                "averageMqPriceInArea",
                "average_mq_price_in_area",
                "valutazione_mercato.quotazione.locazione.med_mq_anno",
                "valutazione_mercato.quotazione.med_mq_anno",
                "quotazione.locazionemed_mq_anno",
                "quotazione.locazione_med_mq_anno",
                "quotazione.locazioneMed_mq_anno",
                "quotazione.locazioneMedMqAnno",
            ],
            assetDetails?.averageMqPriceInArea ?? null
        )

        const aiMinPerMq = aiMinMqAnno !== null
            ? aiMinMqAnno
            : aiPriceMin !== null && assetGla !== null && assetGla > 0
                ? aiPriceMin / assetGla
                : averageMqPriceInArea !== null
                    ? averageMqPriceInArea * 0.67
                    : null

        const aiMaxPerMq = aiMaxMqAnno !== null
            ? aiMaxMqAnno
            : aiPriceMax !== null && assetGla !== null && assetGla > 0
                ? aiPriceMax / assetGla
                : averageMqPriceInArea !== null
                    ? averageMqPriceInArea * 1.38
                    : null

        const priceDeltaLabel = pricePerMq !== null && averageMqPriceInArea !== null && averageMqPriceInArea !== 0
            ? (() => {
                const delta = Math.round(((pricePerMq - averageMqPriceInArea) / averageMqPriceInArea) * 100)
                const sign = delta > 0 ? "+" : ""
                return `${sign}${delta}% vs media`
            })()
            : null

		const averageMqPriceLabel = averageMqPriceInArea !== null
			? `${this.formatCurrency(averageMqPriceInArea, 2)} /mq`
			: "-"
		const aiPriceRangeLabel = aiPriceMin !== null && aiPriceMax !== null
			? `${this.formatCurrency(aiPriceMin)} - ${this.formatCurrency(aiPriceMax)}`
			: "-"
        const benchmarkValues = [
            { label: t({ id: "match-details.pricing.benchmark.asset", message: "Questo immobile" }), value: pricePerMq, color: "#1294aa" },
            { label: t({ id: "match-details.pricing.benchmark.average", message: "Media zona" }), value: averageMqPriceInArea, color: "#6aa0c7" },
            { label: t({ id: "match-details.pricing.benchmark.max", message: "Massimo zona" }), value: aiMaxPerMq, color: "#97a3b3" },
            { label: t({ id: "match-details.pricing.benchmark.min", message: "Minimo zona" }), value: aiMinPerMq, color: "#97a3b3" },
        ].filter((entry) => entry.value !== null) as { label: string; value: number; color: string }[]

        const benchmarkMax = benchmarkValues.reduce((max, entry) => Math.max(max, entry.value), 0)

        const descriptionText = assetDetails?.aiMarkdownResume || asset?.description || t({ id: "match-details.description.missing", message: "Descrizione non disponibile." })

        const totalFootCountDaily = assetDetails?.totalFootCountDaily ?? null
        const pedestrianPassHours = assetDetails?.pedestrianPassHours ?? null
        const weekendTrafficIncrease = assetDetails?.weekendTrafficIncrease ?? null
        const trafficItems = [
            totalFootCountDaily !== null
                ? { icon: <VisibilityOutlinedIcon fontSize="small" />, label: `${this.formatNumber(totalFootCountDaily)} passaggi/giorno` }
                : null,
            pedestrianPassHours
                ? { icon: <AccessTimeIcon fontSize="small" />, label: `Zona pedonale ${pedestrianPassHours}` }
                : null,
            weekendTrafficIncrease !== null
                ? { icon: <TrendingUpIcon fontSize="small" />, label: `${this.formatPercentage(weekendTrafficIncrease, true)} traffico weekend` }
                : null,
        ].filter(Boolean) as { icon: React.ReactNode; label: string }[]

        const poiList = getAiAnalysisValue(aiAnalysis, "poi.lista")
        const poiEntries: PoiEntry[] = Array.isArray(poiList) ? poiList as PoiEntry[] : []
        const poiMarkers = poiEntries.filter(
            (poi): poi is PoiEntry & { lat: number; lng: number } =>
                typeof poi.lat === "number" && typeof poi.lng === "number"
        )

        const assetCoordinates = asset?.location?.coordinates ?? null
        const assetLat = assetCoordinates && assetCoordinates.length >= 2 ? assetCoordinates[1] : null
        const assetLng = assetCoordinates && assetCoordinates.length >= 2 ? assetCoordinates[0] : null
        const mapCenter = assetLat !== null && assetLng !== null
            ? { lat: assetLat, lng: assetLng }
            : (poiMarkers.length > 0 ? { lat: poiMarkers[0].lat, lng: poiMarkers[0].lng } : { lat: 41.9028, lng: 12.4964 })

        const mapZoom = poiMarkers.length > 0 || (assetLat !== null && assetLng !== null) ? 15 : 5
        const googleMapsApiKey = "AIzaSyCJiclrDzpvGD3bqTSp3NHlB4wQ7j-b1XQ"

        const recognizedBrandPois = poiEntries.filter((poi) => this.isPoiBrandRecognized(poi))
        const unrecognizedPois = poiEntries.filter((poi) => !this.isPoiBrandRecognized(poi))
        const isBrandTabActive = this.state.neighborhoodTab === "brand"
        const activeNeighborhoodPois = isBrandTabActive ? recognizedBrandPois : unrecognizedPois
        const neighborhoodListMaxHeight = NEIGHBORHOOD_MAX_VISIBLE_ITEMS * 64

        const documents = this.state.documentsData?.items ?? []
        const sessionBrand = this.props.sessionData.brands?.[0] ?? null
        const userBrandId = match?.brand?.id ?? sessionBrand?.id ?? null
        const userBrandName = match?.brand?.name ?? sessionBrand?.name ?? null
        const aiReportDocument = this.findAiReportDocument(documents, userBrandId ?? null, userBrandName ?? null)
        const aiReportDownloadUrl = asset && aiReportDocument ? `/api/asset/${asset.id}/document/${aiReportDocument.id}` : null
        const isContactSendDisabled = this.state.sendingContactMessage ||
            !canContactAccountManager ||
            !this.state.contactSubject.trim() ||
            !this.state.contactMessageEditorState.getCurrentContent().hasText()

        return (
            <>
                {this.matesComponent.render()}

                <Box sx={{ pt: 2, pb: 3 }}>
                    {userIsBrand && this.state.refuseModalOpened &&
                        <RefuseMatchModal closeModal={this.closeRefuseModal} callback={this.refuseMatchModalCallback} />}
                    {userIsBrand && this.state.acceptModalOpened &&
                        <AcceptMatchModal closeModal={this.closeAcceptModal} callback={this.acceptMatchModalCallback} />}
                    {userIsBrand && (
                        <Dialog
                            open={this.state.interestModalOpened}
                            onClose={this.closeInterestModal}
                            maxWidth="md"
                            fullWidth
                        >
                            <DialogTitle>Comunica interesse</DialogTitle>
                            <DialogContent>
                                <DialogContentText component="div" sx={{ mb: 2 }}>
                                    <Box
                                        sx={{ color: "text.secondary", lineHeight: 1.7 }}
                                        dangerouslySetInnerHTML={{ __html: this.interestDisclaimerHtml }}
                                    />
                                </DialogContentText>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.interestDisclaimerAccepted}
                                            onChange={this.handleInterestDisclaimerChange}
                                        />
                                    }
                                    label="Ho preso visione dell'informativa"
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.closeInterestModal} disabled={this.state.interestSubmitting}>
                                    <Trans id="commons.actions.cancel">Annulla</Trans>
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={this.confirmInterest}
                                    disabled={!this.state.interestDisclaimerAccepted || this.state.interestSubmitting}
                                >
                                    Comunica interesse
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    <Loading show={this.state.loading} />

                    <PageHeader
                        title={RoutesData.MATCH_DETAILS.title}
                        paths={[RoutesData.DASHBOARD, RoutesData.MATCH_DETAILS]}
                        params={{ "MATCH_DETAILS": { "id": this.matchId } }}
                    />

                    {!this.state.loading && !match && (
                        <Typography sx={{ px: 2, py: 2 }} color="text.secondary">
                            <Trans id="match-details.not-found">Match non disponibile.</Trans>
                        </Typography>
                    )}

                    {!this.state.loading && match && asset && (
                        <Stack spacing={2} sx={{ mt: 1, borderRadius: 1 }}>
                            <MatchOverviewSection
                                compatibilityValue={compatibilityValue}
                                matchReasons={matchReasons}
                                matchStatusMessage={matchStatusMessage}
                                rejectionReason={rejectionReason ?? null}
                                status={match.status}
                                userIsBrand={userIsBrand}
                                canContactAccountManager={canContactAccountManager}
                                primaryAction={primaryAction}
                                canRefuse={canRefuse}
                                onOpenRefuseModal={this.openRefuseMatchModal}
                                onOpenContactModal={this.openContactModal}
                                renderCompatibilityBar={this.renderCompatibilityBar}
                                renderCompatibilityEmoticon={this.renderCompatibilityEmoticon}
                            />

							<Box sx={blurredSectionSx}>
								<MatchAssetSummarySection
									asset={asset}
									sessionData={this.props.sessionData}
									zoneLabel={zoneLabel}
									assetTitle={assetTitle}
									priceLabel={priceLabel}
									pricePerMqLabel={pricePerMqLabel}
									highlightChips={highlightChips}
									featureChips={featureChips}
									bathroomsValue={bathroomsValue}
									photoData={this.state.photoData}
									userIsBrand={userIsBrand}
									canContactAccountManager={canContactAccountManager}
									onOpenContactModal={this.openContactModal}
									formatNumber={this.formatNumber}
								/>
                            </Box>

                            <Box sx={{ border: "1px solid #d4d9e3", backgroundColor: "white", p: 3, ...blurredSectionSx }}>
                                <Typography variant="h5" fontWeight={700} sx={{ color: "#25324a" }}>
                                    <Trans id="match-details.analysis.title">Analisi dell'immobile</Trans>
                                </Typography>
                                <Typography color="text.secondary" sx={{ mb: 2 }}>
                                    <Trans id="match-details.analysis.last-update">Ultimo aggiornamento: {lastUpdateLabel}</Trans>
                                </Typography>

                                <Box sx={{ border: "2px solid #a7a9ff", p: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                                        <Trans id="match-details.analysis.description">Descrizione</Trans>
                                    </Typography>
                                    <Typography sx={{ mt: 1.5, color: "#5f6775", lineHeight: 1.7 }}>
                                        {descriptionText}
                                    </Typography>

                                    {trafficItems.length > 0 && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                                                {trafficItems.map((item, index) => (
                                                    <Stack key={`traffic-${index}`} direction="row" spacing={1} alignItems="center">
                                                        <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
                                                            {item.icon}
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </Box>

                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} md={9}>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                                            <Avatar sx={{ bgcolor: "#173e8d", width: 52, height: 52 }}>
                                                <MapOutlinedIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h5" fontWeight={700} sx={{ color: "#25324a" }}>
                                                    <Trans id="match-details.neighborhood.title">Analisi del quartiere</Trans>
                                                </Typography>
                                                <Typography color="text.secondary">{zoneLabel}</Typography>
                                            </Box>
                                        </Stack>

                                        <Box sx={{ height: 360, border: "1px solid #d4d9e3", backgroundColor: "#edf1f6" }}>
                                            <LoadScript googleMapsApiKey={googleMapsApiKey}>
                                                <GoogleMap
                                                    id="match-neighborhood-map"
                                                    mapContainerStyle={{ height: "100%", width: "100%" }}
                                                    center={mapCenter}
                                                    zoom={mapZoom}
                                                    options={{
                                                        streetViewControl: false,
                                                        mapTypeControl: false,
                                                        fullscreenControl: false,
                                                    }}
                                                    onClick={() => this.setState({ selectedPoiIndex: null })}
                                                >
                                                    {assetLat !== null && assetLng !== null && (
                                                        <Marker
                                                            position={{ lat: assetLat, lng: assetLng }}
                                                            icon="//maps.google.com/mapfiles/ms/icons/blue-dot.png"
                                                        />
                                                    )}
													{poiMarkers.map((poi, index) => (
														<Marker
															key={poi.place_id || `${poi.name ?? "poi"}-${index}`}
															position={{ lat: poi.lat, lng: poi.lng }}
															title={this.getPoiName(poi) ?? undefined}
															onClick={() => this.setState({ selectedPoiIndex: index })}
														/>
													))}
                                                    {this.state.selectedPoiIndex !== null && poiMarkers[this.state.selectedPoiIndex] && (
                                                        <InfoWindow
                                                            position={{
                                                                lat: poiMarkers[this.state.selectedPoiIndex].lat,
                                                                lng: poiMarkers[this.state.selectedPoiIndex].lng,
                                                            }}
                                                            onCloseClick={() => this.setState({ selectedPoiIndex: null })}
														>
															<Typography variant="body2" fontWeight={600}>
																{this.getPoiName(poiMarkers[this.state.selectedPoiIndex]) ?? "-"}
															</Typography>
														</InfoWindow>
                                                    )}
                                                </GoogleMap>
                                            </LoadScript>
                                        </Box>
                                    </Grid>

									<Grid item xs={12} md={3}>
										<Box sx={{ pl: { md: 2 }, borderLeft: { md: "1px solid #d4d9e3" }, height: "100%" }}>
											<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
												<Avatar sx={{ bgcolor: "#173e8d", width: 52, height: 52 }}>
													<VisibilityOutlinedIcon />
												</Avatar>
												<Box>
													<Typography variant="h6" fontWeight={700} sx={{ color: "#25324a" }}>
														<Trans id="match-details.neighborhood.brands">Altri Brand nell'area</Trans>
													</Typography>
													<Typography color="text.secondary">
														<Trans id="match-details.neighborhood.radius">Raggio 250m</Trans>
													</Typography>
												</Box>
											</Stack>
											<Tabs
												value={this.state.neighborhoodTab}
												onChange={this.handleNeighborhoodTabChange}
												variant="fullWidth"
												textColor="primary"
												indicatorColor="primary"
												aria-label="Brand e POI"
												sx={{
													minHeight: 36,
													"& .MuiTab-root": { minHeight: 36, fontSize: 14, fontWeight: 600 },
												}}
											>
												<Tab value="brand" label={<Trans id="asset-details.neighborhood.tab.brand">Brand</Trans>} />
												<Tab value="poi" label={<Trans id="asset-details.neighborhood.tab.poi">POI</Trans>} />
											</Tabs>
											<Stack
												spacing={1}
												sx={{
													mt: 1,
													maxHeight: neighborhoodListMaxHeight,
													overflowY: "auto",
													pr: 0.5,
												}}
											>
												{activeNeighborhoodPois.length > 0 ? (
													activeNeighborhoodPois.map((poi, index) => {
														const primaryLabel = (isBrandTabActive ? this.getPoiName(poi, true) : this.getPoiName(poi)) ?? "-"
														const addressLabel = this.getPoiAddress(poi) ?? "-"
														const avatarLabel = primaryLabel !== "-" ? primaryLabel.charAt(0).toUpperCase() : "?"
														return (
															<Stack
																key={poi.place_id || `${primaryLabel}-${index}`}
																direction="row"
																spacing={1.2}
																alignItems="center"
																sx={{ p: 1.2, border: "1px solid #e3e7ef", backgroundColor: "#f8f9fd", borderRadius: 1 }}
															>
																<Avatar sx={{ width: 36, height: 36, bgcolor: "#173e8d" }}>
																	{avatarLabel}
																</Avatar>
																<Box sx={{ flex: 1, minWidth: 0 }}>
																	<Typography fontWeight={600} noWrap>{primaryLabel}</Typography>
																	<Typography variant="caption" color="text.secondary">{addressLabel}</Typography>
																</Box>
															</Stack>
														)
													})
												) : (
													<Typography color="text.secondary">
														{isBrandTabActive ? (
															<Trans id="asset-details.neighborhood.brands.empty">Nessun brand disponibile.</Trans>
														) : (
															<Trans id="asset-details.neighborhood.poi.empty">Nessun punto di interesse disponibile.</Trans>
														)}
													</Typography>
												)}
											</Stack>
										</Box>
									</Grid>
                                </Grid>
                            </Box>

                            {(aiPriceMin !== null || aiPriceMax !== null || averageMqPriceInArea !== null || priceValue !== null) && (
                                <Box sx={{ border: "1px solid #d4d9e3", backgroundColor: "white", p: 2.5, ...blurredSectionSx }}>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: "#25324a" }}>
                                        <Trans id="match-details.pricing.title">Valutazione dell'immobile</Trans>
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ mb: 2.5 }}>
                                        <Trans id="match-details.pricing.subtitle">Analisi comparativa AI-powered</Trans>
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <Box sx={{ p: 2, background: "linear-gradient(140deg, #ccefd4 0%, #a9ddbf 100%)" }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                                    <Trans id="match-details.pricing.ai-estimate">VALORE STIMATO AI</Trans>
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: "#0f8b8d" }}>
													{aiPriceRangeLabel}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Box sx={{ p: 2, backgroundColor: "#f3f5f9" }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                                    <Trans id="match-details.pricing.requested">PREZZO RICHIESTO</Trans>
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: "#1f2d4a" }}>{priceLabel}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Box sx={{ p: 2, backgroundColor: "#f3f5f9" }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                                    <Trans id="match-details.pricing.area-average">MEDIA ZONA</Trans>
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: "#1f2d4a" }}>
													{averageMqPriceLabel}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Box sx={{ p: 2, backgroundColor: "#f3f5f9" }}>
                                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                                    <Trans id="match-details.pricing.this-asset">QUESTO IMMOBILE</Trans>
                                                </Typography>
                                                <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: "#1f2d4a" }}>{pricePerMqLabel}</Typography>
                                                {priceDeltaLabel && (
                                                    <Chip
                                                        label={priceDeltaLabel}
                                                        size="small"
                                                        sx={{ mt: 1, backgroundColor: "#ccefe8", color: "#0f8b8d" }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {benchmarkValues.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="h6" fontWeight={700} sx={{ color: "#25324a", mb: 2 }}>
                                                <Trans id="match-details.pricing.benchmark.title">Benchmark Prezzi - {zoneLabel}</Trans>
                                            </Typography>
                                            <Stack spacing={1.75}>
                                                {benchmarkValues.map((entry) => {
                                                    const width = benchmarkMax > 0 ? Math.round((entry.value / benchmarkMax) * 100) : 0

                                                    return (
                                                        <Box key={entry.label}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.4 }}>
                                                                <Typography color="text.secondary">{entry.label}</Typography>
																<Typography color="text.secondary">
																	{entry.value !== null ? `${this.formatCurrency(entry.value, 2)}/mq` : "-"}
																</Typography>
                                                            </Stack>
                                                            <Box sx={{ height: 20, backgroundColor: "#d7dde7", borderRadius: 999, overflow: "hidden" }}>
                                                                <Box sx={{ width: `${width}%`, height: "100%", backgroundColor: entry.color }} />
                                                            </Box>
                                                        </Box>
                                                    )
                                                })}
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <Box sx={{ backgroundColor: "#2daeb0", color: "white", px: 3, py: 2.25, ...blurredSectionSx }}>
                                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            <Trans id="match-details.deep-sheet.title">Scheda Immobile Approfondita</Trans>
                                        </Typography>
                                        <Typography>
                                            <Trans id="match-details.deep-sheet.subtitle">Report completo con analisi AI, dati catastali, benchmark e insights di mercato</Trans>
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        {aiReportDownloadUrl ? (
                                            <Button
                                                component="a"
                                                href={aiReportDownloadUrl}
                                                download
                                                variant="outlined"
                                                startIcon={<DownloadOutlinedIcon />}
                                                sx={{ textTransform: "none", borderColor: "rgba(255,255,255,0.7)", color: "white" }}
                                            >
                                                <Trans id="match-details.deep-sheet.download">Scarica scheda</Trans>
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                startIcon={<DownloadOutlinedIcon />}
                                                onClick={this.handleRequestAssessment}
                                                disabled={!userIsBrand || this.state.requestingAssessment}
                                                sx={{ textTransform: "none", backgroundColor: "#00a3e5" }}
                                            >
                                                <Trans id="match-details.deep-sheet.request">Richiedi Scheda</Trans>
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Box>

                            <Box sx={blurredSectionSx}>
                                <AssetDocumentsSection
                                    documents={documents}
                                    userIsBrand={true}
                                    assetId={asset.id}
                                    onOpenUploadModal={() => undefined}
                                    onOpenDeleteModal={() => undefined}
                                    formatDate={this.formatDate}
                                />
                            </Box>
                        </Stack>
                    )}
                </Box>

                <SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />

                <Dialog open={this.state.assessmentModalOpened} onClose={this.closeAssessmentModal}>
                    <DialogTitle>
                        <Trans id="match-details.deep-sheet.requested.title">Richiesta inviata</Trans>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <Trans id="match-details.deep-sheet.requested.message">
                                La tua richiesta è stata presa in carico, riceverai una notifica appena la scheda sarà pronta!
                            </Trans>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeAssessmentModal} autoFocus>
                            <Trans id="commons.actions.ok">OK</Trans>
                        </Button>
                    </DialogActions>
                </Dialog>

                <ContactMessageDialog
                    open={this.state.contactModalOpened}
                    onClose={this.closeContactModal}
                    onSend={this.sendContactMessage}
                    sending={this.state.sendingContactMessage}
                    accountManagerName={accountManagerName}
                    subject={this.state.contactSubject}
                    onSubjectChange={(subject) => this.setState({ contactSubject: subject })}
                    editorState={this.state.contactMessageEditorState}
                    onEditorStateChange={this.handleContactMessageEditorChange}
                    canSend={!isContactSendDisabled}
                />
            </>
        )
    }
}

export default withUseRouter(MatchDetails)
